import { Button } from '../crop-circle/Button';
import { SearchIcon, XIcon } from '../crop-circle/icons';
import { 
  colors as cropCircleColors, 
  spacing as cropCircleSpacing, 
  componentBorderRadius 
} from '../crop-circle/tokens';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { useEffect, useState, ReactNode, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  FlatList,
  Platform,
  Pressable,
  StyleSheet,
  Text,
  TextInput as RNTextInput,
  View,
} from 'react-native';

export type SearchPageProps = {
  /** Storage key for persisting recent searches (e.g., 'activity_recent_searches') */
  recentSearchesKey: string;
  /** Current search keyword from filter */
  currentKeyword?: string;
  /** Callback to update search keyword */
  onSearchUpdate: (keyword: string) => Promise<void>;
  /** Placeholder text for search input */
  searchPlaceholder: string;
  /** Translation key for search button */
  searchButtonKey: string;
  /** Translation key for recent searches title */
  recentSearchesTitleKey: string;
  /** Callback when search is submitted (navigates back) */
  onSearchSubmit?: () => void;
  /** Optional footer component (e.g., BottomComponentBar for mobile) */
  footer?: ReactNode;
  /** Maximum number of recent searches to store */
  maxRecentSearches?: number;
  /** Optional ref to expose search trigger function */
  onSearchTrigger?: (triggerSearch: () => Promise<void>) => void;
};

export const SearchPage = ({
  recentSearchesKey,
  currentKeyword = '',
  onSearchUpdate,
  searchPlaceholder,
  searchButtonKey,
  recentSearchesTitleKey,
  onSearchSubmit,
  footer,
  maxRecentSearches = 10,
  onSearchTrigger,
}: SearchPageProps) => {
  const { t } = useTranslation();
  const [text, setText] = useState('');
  const [recentSearches, setRecentSearches] = useState<string[]>([]);
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef<any>(null);

  useEffect(() => {
    if (!text && currentKeyword) {
      setText(currentKeyword);
    }
  }, [currentKeyword]);

  useEffect(() => {
    loadRecentSearches();
  }, []);

  const loadRecentSearches = async () => {
    try {
      const stored = await AsyncStorage.getItem(recentSearchesKey);
      if (stored) {
        setRecentSearches(JSON.parse(stored));
      }
    } catch (error) {
      console.error('Error loading recent searches:', error);
    }
  };

  const saveRecentSearch = useCallback(async (keyword: string) => {
    if (!keyword.trim()) return;
    
    try {
      const updated = [keyword, ...recentSearches.filter(s => s !== keyword)]
        .slice(0, maxRecentSearches);
      await AsyncStorage.setItem(recentSearchesKey, JSON.stringify(updated));
      setRecentSearches(updated);
    } catch (error) {
      console.error('Error saving recent search:', error);
    }
  }, [recentSearches, maxRecentSearches, recentSearchesKey]);

  const handleSearch = useCallback(async (keyword: string) => {
    if (!keyword.trim()) {
      // If empty, just clear the search
      await onSearchUpdate('');
      onSearchSubmit?.();
      return;
    }
    
    setText(keyword);
    // Only update filter and save to history when search is actually submitted
    await onSearchUpdate(keyword);
    await saveRecentSearch(keyword);
    onSearchSubmit?.();
  }, [onSearchUpdate, onSearchSubmit, saveRecentSearch]);

  useEffect(() => {
    // Expose search trigger function to parent if needed
    if (onSearchTrigger) {
      onSearchTrigger(async () => {
        await handleSearch(text);
      });
    }
  }, [text, onSearchTrigger, handleSearch]);

  const clearSearch = () => {
    setText('');
    // Don't update filter when clearing - only update on submit
  };

  const SearchButton = () => {
    return (
      <Button
        style={styles.searchButton}
        onPress={() => {
          if (text.trim()) {
            handleSearch(text);
          } else {
            onSearchSubmit?.();
          }
        }}
        state={text.trim() ? 'active' : 'default'}
      >
        {t(searchButtonKey)}
      </Button>
    );
  };

  // Add style to remove focus outline on web for search input
  useEffect(() => {
    if (Platform.OS === 'web') {
      const styleId = 'search-input-no-focus-outline';
      
      if (!document.getElementById(styleId)) {
        const styleElement = document.createElement('style');
        styleElement.id = styleId;
        styleElement.textContent = `
          input:focus,
          input:focus-visible {
            outline: none !important;
            outline-width: 0 !important;
            outline-color: transparent !important;
          }
        `;
        document.head.appendChild(styleElement);
      }

      return () => {
        const existingStyle = document.getElementById(styleId);
        if (existingStyle) {
          existingStyle.remove();
        }
      };
    }
    return undefined;
  }, []);

  return (
    <View style={styles.container}>
      <View style={styles.content}>
        <View style={styles.searchInputContainer}>
          <View 
            style={[
              cropCircleStyles.inputContainer,
              isFocused && cropCircleStyles.inputContainerFocused,
            ]}
          >
            <View style={cropCircleStyles.iconLeft}>
              <SearchIcon size={20} color={cropCircleColors.neutral[500]} />
            </View>
            <RNTextInput
              ref={inputRef}
              placeholder={searchPlaceholder}
              value={text}
              onChangeText={(text) => setText(text)}
              style={[
                cropCircleStyles.textInput,
                Platform.OS === 'web' && {
                  outline: 'none',
                  outlineWidth: 0,
                  outlineColor: 'transparent',
                } as any,
              ]}
              placeholderTextColor={cropCircleColors.system.textPlaceholder}
              autoFocus={true}
              onFocus={() => {
                setIsFocused(true);
                // Remove focus outline on web
                if (Platform.OS === 'web' && inputRef.current) {
                  const element = inputRef.current as any;
                  const nativeNode = element?._nativeNode || element;
                  if (nativeNode && nativeNode.style) {
                    nativeNode.style.outline = 'none';
                    nativeNode.style.outlineWidth = '0';
                    nativeNode.style.outlineColor = 'transparent';
                  }
                }
              }}
              onBlur={() => setIsFocused(false)}
              onSubmitEditing={() => {
                if (text.trim()) {
                  handleSearch(text);
                }
              }}
            />
            {text ? (
              <Pressable
                onPress={clearSearch}
                style={cropCircleStyles.iconRight}
              >
                <XIcon size={20} color={cropCircleColors.neutral[500]} />
              </Pressable>
            ) : null}
          </View>
        </View>

        {recentSearches.length > 0 && (
          <View style={styles.recentSearchesContainer}>
            <Text style={cropCircleStyles.recentSearchesTitle}>
              {t(recentSearchesTitleKey)}
            </Text>
            <FlatList
              data={recentSearches}
              keyExtractor={(item, index) => `${item}-${index}`}
              renderItem={({ item }) => (
                <Pressable
                  onPress={() => handleSearch(item)}
                  style={cropCircleStyles.recentSearchItem}
                >
                  <Text style={cropCircleStyles.recentSearchText}>{item}</Text>
                </Pressable>
              )}
              contentContainerStyle={styles.recentSearchesList}
            />
          </View>
        )}
      </View>
      {footer || (
        <View style={styles.defaultFooter}>
          <SearchButton />
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: cropCircleColors.neutral[100],
    position: 'relative',
    flex: 1,
  },
  content: {
    flex: 1,
    paddingHorizontal: 16,
    paddingTop: 16,
  },
  searchInputContainer: {
    marginBottom: 24,
  },
  searchButton: {
    flex: 1,
    height: 56,
    maxHeight: 56,
    marginRight: 16,
  },
  recentSearchesContainer: {
    flex: 1,
    paddingTop: 16,
    paddingBottom: 80,
  },
  recentSearchesList: {
    paddingBottom: 20,
  },
  defaultFooter: {
    padding: 16,
  },
});

const cropCircleStyles = StyleSheet.create({
  inputContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: cropCircleColors.neutral[200],
    borderWidth: 1,
    borderColor: cropCircleColors.system.borderDefault,
    borderRadius: componentBorderRadius.input.default,
    paddingHorizontal: cropCircleSpacing.lg,
    height: 56,
  },
  inputContainerFocused: {
    borderColor: cropCircleColors.system.borderDefault,
  },
  iconLeft: {
    marginRight: cropCircleSpacing.sm,
  },
  iconRight: {
    marginLeft: cropCircleSpacing.sm,
    padding: cropCircleSpacing.xs,
  },
  textInput: {
    flex: 1,
    fontSize: 16,
    color: cropCircleColors.system.textPrimary,
    paddingVertical: 0,
  },
  recentSearchesTitle: {
    fontSize: 14,
    fontWeight: '500',
    color: cropCircleColors.system.textSecondary,
    paddingHorizontal: cropCircleSpacing.xl,
    paddingBottom: cropCircleSpacing.md,
    textTransform: 'uppercase',
  },
  recentSearchItem: {
    paddingHorizontal: cropCircleSpacing.xl,
    paddingVertical: cropCircleSpacing.md,
    borderBottomWidth: 1,
    borderBottomColor: cropCircleColors.system.borderDefault,
  },
  recentSearchText: {
    fontSize: 16,
    color: cropCircleColors.system.textPrimary,
  },
});
